/**
 * The `Montant : X EUR` clause of the admin-booking modal, with what the amount
 * is a price OF.
 *
 * The modal rendered a bare `Montant : 10.00 EUR` for a figure whose meaning
 * flips with the *Type* and *Joueurs* controls sitting next to it. A PUBLIC
 * booking charges the person on the payment link ONE SEAT — the other seats are
 * collected from joiners later — so raising *Joueurs* from 2 to 4 roughly halves
 * the number on screen while the court costs the same. An operator reading that
 * figure down the phone understates the court by a factor of `capacity`, and
 * nothing on the screen said which of the two things they were looking at.
 *
 * `payerAmountBasis` is the server's own answer to that question
 * (`resolvePhoneBookingQuote`), and it is the same field the two AI-assistant
 * quoting surfaces have returned since Run 53 / Run 88 — this modal was the one
 * quoting surface that dropped it.
 *
 * Deliberately NOT derived here and NOT shown: the whole-court total. It is
 * `totalAmount × capacity` only approximately — `totalAmount` is the
 * split-rounded cent figure Stripe is actually charged — and inventing a court
 * price that is a cent or two off the truth on an operator's quoting screen is
 * the same class of defect as the one this fixes.
 *
 * An absent `payerAmountBasis` (an API predating the field) returns the exact
 * string the modal rendered before, so an older server changes nothing (D4).
 */
export function buildAdminBookingAmountLabel({
    totalAmount,
    currency,
    payerAmountBasis,
    capacity,
}: {
    totalAmount: number;
    currency: string;
    payerAmountBasis?: 'per_player' | 'whole_court';
    capacity?: number | null;
}): string {
    const amount = `${totalAmount.toFixed(2)} ${String(
        currency ?? '',
    ).toUpperCase()}`;

    if (payerAmountBasis === 'whole_court') {
        return `Montant : ${amount} — terrain complet`;
    }

    if (payerAmountBasis === 'per_player') {
        // The seat count is only quoted when it is one of several. At a
        // capacity of 1 "les 0 autres places" is nonsense, and a non-finite
        // capacity (the dropdown mid-reset) must not print "NaN" onto a
        // money-facing line.
        const seats =
            typeof capacity === 'number' && Number.isFinite(capacity)
                ? capacity
                : null;

        return seats !== null && seats > 1
            ? `Montant : ${amount} — part d'un joueur sur ${seats} (les ${
                  seats - 1
              } autres places sont réglées par les joueurs qui rejoignent)`
            : `Montant : ${amount} — part d'un joueur`;
    }

    return `Montant : ${amount}`;
}
